import { getAgentInboundAnsweringPolicy, getAgentTransferDestinations, } from "@repo/common";
import { collection, collectionGroup, deleteDoc, doc, getDoc, setDoc, Timestamp, } from "firebase/firestore";
import { prepareForFirestore } from "./utils";
import { warnUnknownFields } from "./converterWarnings";
const AGENT_FIELDS = [
    "id",
    "path",
    "companyId",
    "serviceId",
    "name",
    "phoneNumbers",
    "blacklistedCallerNumbers",
    "testerCallerNumbers",
    "transferToHumanPhoneNumber",
    "transferToHumanPhoneNumbers",
    "transferToHumanDestinations",
    "phoneNumberDetails",
    "phoneNumberDetailsSyncedAt",
    "createdAt",
    "updatedAt",
    "lastCall",
    "status",
    "inboundAnsweringPolicy",
    "version",
    "greeting",
    "goal",
    "referenceMaterials",
    "gender",
    "tone",
    "voice",
    "realtime",
    "gptLive",
    "defaultLanguage",
    "timeZone",
    "dos",
    "donts",
    "policySections",
    "messagesSenderName",
    "messages",
    "typicalFlows",
    "hasUncommittedChanges",
    "stagingDeployment",
    "liveDeployment",
    "liveDeploymentVersions",
    "stats",
    "onboarding",
];
function normalizeAgentStatus(value) {
    if (value === "Active" || value === "Paused" || value === "Draft") {
        return value;
    }
    if (value === "active")
        return "Active";
    if (value === "paused" || value === "disabled" || value === "Disabled") {
        return "Paused";
    }
    if (value === "draft")
        return "Draft";
    return "Draft";
}
function normalizeAgentStats(value) {
    const data = value && typeof value === "object"
        ? value
        : {};
    return {
        todayDateKey: typeof data.todayDateKey === "string" ? data.todayDateKey : undefined,
        callsToday: typeof data.callsToday === "number" ? data.callsToday : 0,
        minutesToday: typeof data.minutesToday === "number" ? data.minutesToday : 0,
        totalCalls: typeof data.totalCalls === "number" ? data.totalCalls : 0,
        totalMinutes: typeof data.totalMinutes === "number" ? data.totalMinutes : 0,
        goalsToday: typeof data.goalsToday === "number" ? data.goalsToday : 0,
        totalGoals: typeof data.totalGoals === "number" ? data.totalGoals : 0,
    };
}
function livePhoneNumberDetails(value) {
    if (!value || typeof value !== "object")
        return undefined;
    const details = value;
    return {
        live: Array.isArray(details.live) ? details.live : [],
    };
}
export const agentConverter = {
    toFirestore(value) {
        return prepareForFirestore({
            ...value,
            phoneNumberDetails: livePhoneNumberDetails(value.phoneNumberDetails),
            status: value.status === "Paused" ? "Active" : value.status,
            inboundAnsweringPolicy: getAgentInboundAnsweringPolicy(value),
            transferToHumanDestinations: getAgentTransferDestinations(value),
        });
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        warnUnknownFields("packages/ui Agent.fromFirestore", data, AGENT_FIELDS);
        return {
            id: snapshot.id,
            path: snapshot.ref.path,
            companyId: snapshot.ref.parent.parent?.id || data.companyId || "",
            serviceId: data.serviceId || null,
            name: data.name || snapshot.id,
            phoneNumbers: Array.isArray(data.phoneNumbers)
                ? data.phoneNumbers.filter((value) => typeof value === "string")
                : [],
            phoneNumberDetails: livePhoneNumberDetails(data.phoneNumberDetails),
            blacklistedCallerNumbers: Array.isArray(data.blacklistedCallerNumbers)
                ? data.blacklistedCallerNumbers.filter((value) => typeof value === "string")
                : [],
            testerCallerNumbers: Array.isArray(data.testerCallerNumbers)
                ? data.testerCallerNumbers.filter((value) => typeof value === "string")
                : [],
            transferToHumanPhoneNumber: typeof data.transferToHumanPhoneNumber === "string" &&
                data.transferToHumanPhoneNumber.trim()
                ? data.transferToHumanPhoneNumber.trim()
                : null,
            transferToHumanPhoneNumbers: Array.isArray(data.transferToHumanPhoneNumbers)
                ? data.transferToHumanPhoneNumbers.filter((value) => typeof value === "string" && Boolean(value.trim()))
                : typeof data.transferToHumanPhoneNumber === "string" &&
                    data.transferToHumanPhoneNumber.trim()
                    ? [data.transferToHumanPhoneNumber.trim()]
                    : [],
            transferToHumanDestinations: getAgentTransferDestinations({
                transferToHumanDestinations: data.transferToHumanDestinations,
                transferToHumanPhoneNumbers: data.transferToHumanPhoneNumbers,
                transferToHumanPhoneNumber: data.transferToHumanPhoneNumber,
            }),
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate().toISOString()
                : data.createdAt || null,
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate().toISOString()
                : data.updatedAt || null,
            lastCall: data.lastCall instanceof Timestamp
                ? data.lastCall.toDate().toISOString()
                : data.lastCall || null,
            status: normalizeAgentStatus(data.status) === "Paused"
                ? "Active"
                : normalizeAgentStatus(data.status),
            inboundAnsweringPolicy: getAgentInboundAnsweringPolicy({
                inboundAnsweringPolicy: data.inboundAnsweringPolicy,
                status: data.status,
            }),
            version: typeof data.version === "number" && Number.isFinite(data.version)
                ? data.version
                : 1,
            greeting: data.greeting || "",
            goal: data.goal || "",
            referenceMaterials: data.referenceMaterials || [],
            gender: data.gender || "",
            tone: data.tone || "",
            voice: data.voice || "",
            realtime: data.realtime && typeof data.realtime === "object"
                ? {
                    provider: typeof data.realtime.provider ===
                        "string"
                        ? String(data.realtime.provider)
                        : undefined,
                    model: typeof data.realtime.model ===
                        "string"
                        ? String(data.realtime.model)
                        : undefined,
                    effort: typeof data.realtime.effort ===
                        "string"
                        ? data.realtime.effort
                        : undefined,
                }
                : undefined,
            gptLive: data.gptLive && typeof data.gptLive === "object"
                ? {
                    enabled: data.gptLive.enabled === true,
                    voice: typeof data.gptLive.voice === "string"
                        ? data.gptLive.voice
                        : undefined,
                    reasoningModel: typeof data.gptLive.reasoningModel === "string"
                        ? data.gptLive.reasoningModel
                        : undefined,
                }
                : undefined,
            defaultLanguage: data.defaultLanguage || "",
            timeZone: data.timeZone || "Asia/Jerusalem",
            dos: data.dos || "",
            donts: data.donts || "",
            policySections: data.policySections || [],
            messagesSenderName: data.messagesSenderName || "",
            messages: data.messages || [],
            typicalFlows: data.typicalFlows || [],
            service: null,
            hasUncommittedChanges: data.hasUncommittedChanges ?? false,
            stagingDeployment: data.stagingDeployment || undefined,
            liveDeployment: data.liveDeployment || undefined,
            liveDeploymentVersions: data.liveDeploymentVersions || [],
            stats: normalizeAgentStats(data.stats),
        };
    },
};
export function agentsCollection(db, companyId) {
    return collection(db, "companies", companyId, "agents").withConverter(agentConverter);
}
export function allAgentsQuery(db) {
    return collectionGroup(db, "agents").withConverter(agentConverter);
}
export function agentDocument(db, companyId, agentId) {
    return doc(db, "companies", companyId, "agents", agentId).withConverter(agentConverter);
}
export async function fetchAgent(db, companyId, agentId) {
    const snapshot = await getDoc(agentDocument(db, companyId, agentId));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function createAgent(db, data) {
    if (!data.companyId) {
        throw new Error("Company ID is required to create an agent");
    }
    const docRef = doc(agentsCollection(db, data.companyId));
    const agentData = { ...data };
    delete agentData.service;
    await setDoc(docRef, {
        ...agentData,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastCall: null,
    });
    return docRef.id;
}
export async function updateAgent(db, companyId, agentId, data) {
    const docRef = agentDocument(db, companyId, agentId);
    const agentData = { ...data };
    delete agentData.service;
    await setDoc(docRef, {
        ...agentData,
        updatedAt: new Date(),
    }, { merge: true });
}
export async function deleteAgent(db, companyId, agentId) {
    await deleteDoc(agentDocument(db, companyId, agentId));
}
